/**
 * Gallery Swipe Navigation Module
 * Adds swipe functionality to both product gallery and overlay gallery
 * Works with existing click navigation
 */
class GallerySwipeNavigation {
  constructor() {
    // Main configuration
    this.config = {
      swipeThreshold: 50, // Minimum swipe distance to trigger navigation
      maxSwipeTime: 300, // Maximum time in ms for a swipe to be considered valid
      preventDefaultOnSwipe: true, // Prevent default browser behavior on swipe
      gallerySelector: '.product__media-list', // Main product gallery
      overlaySelector: '.overlay-gallery__slider', // Overlay gallery slider
      activeMediaSelector: '.product__media-item.is-active', // Active media item selector
      enableLogging: false // Set to true to enable debug logging
    };

    // State tracking
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.touchStartTime = 0;
    this.isSwiping = false;
    this.swipeHandled = false;

    // Initialize
    this.init();
  }

  init() {
    // Find gallery elements
    this.productGallery = document.querySelector(this.config.gallerySelector);
    this.overlayGallery = document.querySelector(this.config.overlaySelector);

    // Add swipe handlers to product gallery
    if (this.productGallery) {
      this.setupSwipeEvents(this.productGallery, this.handleProductGallerySwipe.bind(this));
    }

    // Add swipe handlers to overlay gallery
    if (this.overlayGallery) {
      this.setupSwipeEvents(this.overlayGallery, this.handleOverlayGallerySwipe.bind(this));
    }

    // Set up a mutation observer to detect when the overlay gallery appears
    this.setupOverlayObserver();

    // Handle resize events to reset touch state
    window.addEventListener('resize', () => {
      this.resetTouchState();
    });

    // Listen for changes to the gallery or overlay
    document.addEventListener('variant:changed', () => {
      // Reset and reinitialize on variant changes
      setTimeout(() => {
        this.resetTouchState();
        this.refreshGalleryElements();
      }, 100);
    });
  }

  refreshGalleryElements() {
    // Refresh element references in case DOM has changed
    this.productGallery = document.querySelector(this.config.gallerySelector);
    this.overlayGallery = document.querySelector(this.config.overlaySelector);

    // Reload swipe handlers if needed
    if (this.productGallery && !this.productGallery._hasSwipeHandlers) {
      this.setupSwipeEvents(this.productGallery, this.handleProductGallerySwipe.bind(this));
    }

    if (this.overlayGallery && !this.overlayGallery._hasSwipeHandlers) {
      this.setupSwipeEvents(this.overlayGallery, this.handleOverlayGallerySwipe.bind(this));
    }
  }

  setupOverlayObserver() {
    // Create a MutationObserver to detect when the overlay gallery is shown
    if ('MutationObserver' in window) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Check if the overlay has been shown
          if (mutation.target.classList && 
              mutation.target.classList.contains('overlay-gallery') && 
              !mutation.target.classList.contains('hidden')) {
            
            // Overlay was just shown, ensure it has swipe handlers
            const overlaySlider = mutation.target.querySelector(this.config.overlaySelector);
            if (overlaySlider && !overlaySlider._hasSwipeHandlers) {
              this.setupSwipeEvents(overlaySlider, this.handleOverlayGallerySwipe.bind(this));
            }
          }
        });
      });

      // Observe the body for changes to the overlay's hidden class
      const overlayElement = document.querySelector('.overlay-gallery');
      if (overlayElement) {
        observer.observe(overlayElement, { 
          attributes: true,
          attributeFilter: ['class']
        });
      }
    }
  }

  setupSwipeEvents(element, handler) {
    if (!element || element._hasSwipeHandlers) return;

    // Mark the element to prevent duplicate handlers
    element._hasSwipeHandlers = true;

    // Touch start
    element.addEventListener('touchstart', (e) => {
      // Store initial touch position and time
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartTime = Date.now();
      this.isSwiping = true;
      this.swipeHandled = false;
    }, { passive: !this.config.preventDefaultOnSwipe });

    // Touch move
    element.addEventListener('touchmove', (e) => {
      if (!this.isSwiping) return;
      
      // Update current position
      this.touchEndX = e.touches[0].clientX;
      this.touchEndY = e.touches[0].clientY;
      
      // Calculate horizontal and vertical differences
      const diffX = this.touchStartX - this.touchEndX;
      const diffY = this.touchStartY - this.touchEndY;
      
      // If horizontal swipe is significantly larger than vertical, prevent scrolling
      if (Math.abs(diffX) > Math.abs(diffY) * 1.5 && Math.abs(diffX) > 20) {
        if (this.config.preventDefaultOnSwipe) {
          e.preventDefault();
        }
      }
    }, { passive: !this.config.preventDefaultOnSwipe });

    // Touch end
    element.addEventListener('touchend', (e) => {
      if (!this.isSwiping || this.swipeHandled) return;
      
      // Get final touch position
      this.touchEndX = e.changedTouches[0].clientX;
      this.touchEndY = e.changedTouches[0].clientY;
      
      // Calculate swipe direction and distance
      const diffX = this.touchStartX - this.touchEndX;
      const diffY = this.touchStartY - this.touchEndY;
      const elapsedTime = Date.now() - this.touchStartTime;
      
      // Check if it's a horizontal swipe
      if (Math.abs(diffX) > Math.abs(diffY) * 1.5 && 
          Math.abs(diffX) > this.config.swipeThreshold && 
          elapsedTime < this.config.maxSwipeTime) {
        
        this.swipeHandled = true;
        
        // Determine direction and call handler
        if (diffX > 0) {
          // Swipe left -> next
          handler('next');
        } else {
          // Swipe right -> prev
          handler('prev');
        }
        
        // If we prevented default, we should stop propagation as well
        if (this.config.preventDefaultOnSwipe) {
          e.stopPropagation();
        }
      }
      
      // Reset swiping flag
      this.isSwiping = false;
    }, { passive: !this.config.preventDefaultOnSwipe });

    // Touch cancel
    element.addEventListener('touchcancel', () => {
      this.resetTouchState();
    });
  }

  resetTouchState() {
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.touchStartTime = 0;
    this.isSwiping = false;
    this.swipeHandled = false;
  }

  handleProductGallerySwipe(direction) {    
    // Use existing gallery navigation if available
    if (window.galleryNavigation) {
      // Set navigating flag to true to prevent multiple navigations
      window.galleryNavigation.isNavigating = true;
      
      // Call the navigate method
      window.galleryNavigation.navigate(direction);
      
      // Reset the flag after a delay
      setTimeout(() => {
        window.galleryNavigation.isNavigating = false;
      }, 300);
    } else {
      // Fallback to clicking the navigation arrows
      const button = direction === 'next' 
        ? document.querySelector('.gallery-nav-arrow.gallery-nav-next')
        : document.querySelector('.gallery-nav-arrow.gallery-nav-prev');
      
      if (button) {
        button.click();
      } else {
        this.navigateGalleryFallback(direction);
      }
    }
  }

  handleOverlayGallerySwipe(direction) {    
    // Use gallery overlay manager if available
    if (window.galleryOverlay) {
      window.galleryOverlay.navigate(direction === 'next' ? 1 : -1);
    } else {
      // Fallback to clicking overlay navigation buttons
      const button = direction === 'next' 
        ? document.querySelector('.overlay-gallery__next')
        : document.querySelector('.overlay-gallery__prev');
      
      if (button) {
        button.click();
      }
    }
  }

  navigateGalleryFallback(direction) {
    // Last resort fallback navigation    
    // Get all visible thumbnails
    const thumbnails = Array.from(document.querySelectorAll('.thumbnail-list__item'))
      .filter(thumb => window.getComputedStyle(thumb).display !== 'none');
    
    if (thumbnails.length <= 1) {
      return;
    }
    
    // Find the active thumbnail
    const activeThumb = thumbnails.find(thumb => thumb.classList.contains('is-active'));
    if (!activeThumb) {
      thumbnails[0].click();
      return;
    }
    
    // Get the current index
    const currentIndex = thumbnails.indexOf(activeThumb);
    if (currentIndex === -1) {
      thumbnails[0].click();
      return;
    }
    
    // Calculate the target index
    let targetIndex;
    if (direction === 'next') {
      targetIndex = (currentIndex + 1) % thumbnails.length;
    } else {
      targetIndex = (currentIndex - 1 + thumbnails.length) % thumbnails.length;
    }
    
    // Click the target thumbnail
    if (thumbnails[targetIndex]) {
      thumbnails[targetIndex].click();
    }
  }
}

// Create global instance for access from other scripts
window.gallerySwipeNavigation = null;

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  
  // Helper function to create the gallery swipe navigation
  const initGallerySwipe = () => {
    if (!window.gallerySwipeNavigation) {
      window.gallerySwipeNavigation = new GallerySwipeNavigation();
    }
  };
  
  // First attempt at initialization
  initGallerySwipe();
  
  // Try again after a delay to ensure DOM is ready
  setTimeout(initGallerySwipe, 1000);
  
  // Final attempt after page load
  window.addEventListener('load', () => {
    initGallerySwipe();
    
    // Refresh elements with fresh DOM
    if (window.gallerySwipeNavigation) {
      window.gallerySwipeNavigation.refreshGalleryElements();
    }
  });
});

// Add a helper function for mobile detection
window.isMobileDevice = function() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
};